/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { updateCart } from "@insite/client-framework/Services/CartService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetIsPreloadingData";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedPickUpDate";
import setShippingMethod from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetShippingMethod";
import translate from "@insite/client-framework/Translate";
import { CarrierDto, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Radio from "@insite/mobius/Radio/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup/RadioGroup";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea/TextArea";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xAppState, xCheckoutHandlers } from "@monsma/App/Store/Reducers";
import { StepperContext } from "@monsma/App/Widgets/CheckoutStepper/StepperContext";
import Notify from "@monsma/XDL/Components/Notify/Notify";
import React, { ChangeEvent, FC, useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: xAppState) => {
    const settingsCollection = getSettingsCollection(state);
    const { session } = state.context;
    const { cartId, requestedDeliveryDate, requestedPickupDate } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    return {
        cart: cartState.value,
        showShippingMethod: session.fulfillmentMethod === FulfillmentMethod.Ship,
        showPickUpDate:
            settingsCollection.accountSettings.enableWarehousePickup &&
            settingsCollection.cartSettings.enableRequestPickUpDate &&
            session.fulfillmentMethod === FulfillmentMethod.PickUp,
        session,
        accountSettings: settingsCollection.accountSettings,
        cartSettings: settingsCollection.cartSettings,
        allowEmptyShipping: settingsCollection.accountSettings.allowEmptyShipping,
        // custom
        shippingMethodsToShowOptionsListFor: (settingsCollection?.customSettings as any)
            ?.shippingMethodsToShowOptionsListFor as string[],
        deliveryMessaging: state.xCheckout.deliveryMessaging,
        ltlDeliveryType: state.xCheckout.ltlDeliveryType,
        appointmentRequired: state.xCheckout.appointmentRequired,
        liftGateRequired: state.xCheckout.liftGateRequired,
        requestedDeliveryDate,
        requestedPickupDate,
    };
};

const { setDeliveryMessaging, setLtlDeliveryType, setAppointmentRequired, setLiftGateRequired } = xCheckoutHandlers;

const mapDispatchToProps = {
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setIsPreloadingData,
    // custom
    setDeliveryMessaging,
    setLtlDeliveryType,
    setAppointmentRequired,
    setLiftGateRequired,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface XStyle {
    deliveryDateContainer?: GridItemProps;
    deliveryDateWrapper?: InjectableCss;
    freightWrapper?: InjectableCss;
    freightTitle?: TypographyPresentationProps;
    freightQuestionsTitle?: TypographyPresentationProps;
    freightQuestionsRadioGroup?: RadioGroupPresentationProps;
    freightMessage?: TypographyPresentationProps;
    notifyWrapper?: GridItemProps;
    shippingNotesWrapper?: GridItemProps;
    shippingNotesTextArea?: TextAreaProps;
}

export interface CheckoutShippingCarrierServiceStyles {
    container?: GridContainerProps;
    shipCompleteCheckbox?: CheckboxProps;
    shipCompleteCheckboxGroup?: CheckboxGroupComponentProps;
    centeringWrapper?: InjectableCss;
    noCarriersFoundGridItem?: GridItemProps;
    noCarriersFoundText?: TypographyPresentationProps;
    carrierGridItem?: GridItemProps;
    carrierSelect?: SelectPresentationProps;
    serviceGridItem?: GridItemProps;
    serviceSelect?: SelectPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDatePicker?: DatePickerPresentationProps;
    // custom
    xStyle?: XStyle;
    customNotifyWrapper?: GridItemProps;
}

export const checkoutShippingCarrierServiceStyles: CheckoutShippingCarrierServiceStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    shipCompleteCheckbox: {
        css: css`
            margin-top: 10px;

            span[role="checkbox"] {
                border: 1px solid #888888;
            }
        `,
    },
    noCarriersFoundGridItem: { width: 12 },
    carrierGridItem: { width: [6, 6, 6, 3, 3] },
    serviceGridItem: { width: [6, 6, 6, 3, 3] },
    deliveryDateGridItem: { width: [12, 12, 12, 6, 6] },
    pickUpDateGridItem: { width: [12, 12, 12, 6, 6] },
    customNotifyWrapper: {
        css: css`
            flex-flow: column;
        `,
    },
};

/**
 * @deprecated Use checkoutShippingCarrierServiceStyles instead.
 */
export const checkoutShippingCarrierService = checkoutShippingCarrierServiceStyles;
const styles = checkoutShippingCarrierServiceStyles;

const CheckoutReviewAndSubmitCarrierService: FC<Props> = ({
    cart,
    cartSettings,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    showShippingMethod,
    setShippingMethod,
    showPickUpDate,
    setIsPreloadingData,
    allowEmptyShipping,
    // custom
    requestedDeliveryDate,
    requestedPickupDate,
    shippingMethodsToShowOptionsListFor,

    deliveryMessaging,
    setDeliveryMessaging,

    ltlDeliveryType,
    setLtlDeliveryType,

    appointmentRequired,
    setAppointmentRequired,

    liftGateRequired,
    setLiftGateRequired,
}) => {
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierDto | null>(null);
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    // xng-custom: checking and validating before going to the next step
    const [isFreightValid, setIsFreightValid] = useState<boolean>(true);
    const [shipComplete, setShipComplete] = useState<boolean>(false);
    const { currentStep, preventStepperChange } = useContext(StepperContext);
    const validateCarrier = (from: number, to: number) => {
        if (from === 2 && to >= 3) {
            if (
                selectedShipVia?.description &&
                shippingMethodsToShowOptionsListFor?.includes(selectedShipVia?.description) &&
                (!ltlDeliveryType || !appointmentRequired || !liftGateRequired)
            ) {
                setIsFreightValid(false);
                return true;
            }
            setIsFreightValid(true);
        }
    };
    const validateCarrierRef = useRef(validateCarrier);

    validateCarrierRef.current = validateCarrier;
    useEffect(() => {
        if (cart) {
            setSelectedCarrier(cart.carrier);
            setSelectedShipVia(cart.shipVia);
        }
    }, [cart?.carrier, cart?.shipVia]);

    useEffect(() => preventStepperChange(validateCarrierRef, 2), []);

    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) || !cart.carriers) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner data-test-selector="checkoutShipping_carrierServiceLoading" />
            </StyledWrapper>
        );
    }

    const carrierChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        const selectedCarrier = cart.carriers!.find(c => c.id === event.currentTarget.value)!;
        const defaultShipVias = selectedCarrier.shipVias!.filter(o => o.isDefault);
        const shipVia = defaultShipVias.length > 0 ? defaultShipVias[0] : selectedCarrier.shipVias![0];
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedCarrier(selectedCarrier);
        setSelectedShipVia(shipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };
    const shipViaChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        if (!selectedCarrier) {
            return;
        }
        const selectedShipVia = selectedCarrier.shipVias!.find(s => s.id === event.currentTarget.value)!;
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedShipVia(selectedShipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia: selectedShipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };

    const handleRequestPickUpDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedPickUpDate({
            requestedPickUpDate: selectedDay,
            requestedPickUpDateDisabled: selectedDayDisabled,
        });
    };
    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });
    };

    const handleShipComplete = async (value: any) => {
        setShipComplete(value);
        const newCart = {
            ...cart,
            properties: {
                ...cart.properties,
                shipComplete: value,
            },
        };
        await updateCart({ cart: newCart });
    };

    // end-xng-custom

    return (
        <GridContainer {...styles.container}>
            {showShippingMethod && currentStep === 2 && (
                <>
                    {cart?.carriers?.length === 0 && !allowEmptyShipping && (
                        <GridItem {...styles.noCarriersFoundGridItem}>
                            <Typography {...styles.noCarriersFoundText}>
                                {siteMessage("ReviewAndPay_NoCarriersFound")}
                            </Typography>
                        </GridItem>
                    )}
                    {cart?.carriers && cart?.carriers?.length > 0 && (
                        <>
                            <GridItem {...styles.carrierGridItem}>
                                <Typography variant="h5">{translate("Shipping Options")}</Typography>
                                <Select
                                    label={translate("Select Shipping Option")}
                                    {...styles.carrierSelect}
                                    value={selectedCarrier?.id || ""}
                                    onChange={carrierChangeHandler}
                                    data-test-selector="checkoutReviewAndSubmit_carrierSelect"
                                    name="carrier"
                                >
                                    {cart.carriers!.map(c => {
                                        const id = c.id!.toString();
                                        return (
                                            <option key={id} value={id}>
                                                {c.description}
                                            </option>
                                        );
                                    })}
                                </Select>
                                <CheckboxGroup>
                                    <Checkbox
                                        uid="shipComplete"
                                        {...styles.shipCompleteCheckbox}
                                        checked={shipComplete}
                                        onChange={(e, value) => handleShipComplete(value)}
                                    >
                                        {translate("Ship Complete")}
                                    </Checkbox>
                                </CheckboxGroup>
                                {shippingMethodsToShowOptionsListFor &&
                                    selectedShipVia?.id &&
                                    shippingMethodsToShowOptionsListFor.includes(selectedShipVia?.description) && (
                                        <StyledWrapper {...styles?.xStyle?.freightWrapper}>
                                            <Typography variant="h5" {...styles?.xStyle?.freightTitle}>
                                                {translate("LTL Freight Shipping")}
                                            </Typography>

                                            <Typography variant="p" {...styles?.xStyle?.freightQuestionsTitle}>
                                                {translate("Please select the business type for delivery:")}
                                            </Typography>
                                            <RadioGroup
                                                {...styles?.xStyle?.freightQuestionsRadioGroup}
                                                value={ltlDeliveryType}
                                                onChangeHandler={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                    setLtlDeliveryType(event.target.value);
                                                }}
                                            >
                                                <Radio value="Residential">{translate("Residential")}</Radio>
                                                <Radio value="Commercial">{translate("Commercial")}</Radio>
                                            </RadioGroup>

                                            <Typography variant="p" {...styles?.xStyle?.freightQuestionsTitle}>
                                                {translate("Is an appointment for delivery required?")}
                                            </Typography>
                                            <RadioGroup
                                                {...styles?.xStyle?.freightQuestionsRadioGroup}
                                                value={appointmentRequired}
                                                onChangeHandler={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                    setAppointmentRequired(event.target.value);
                                                }}
                                            >
                                                <Radio value="True">{translate("Yes")}</Radio>
                                                <Radio value="False">{translate("No")}</Radio>
                                            </RadioGroup>

                                            <Typography variant="p" {...styles?.xStyle?.freightQuestionsTitle}>
                                                {translate("Does the shipping location have a lift gate available?")}
                                            </Typography>
                                            <RadioGroup
                                                {...styles?.xStyle?.freightQuestionsRadioGroup}
                                                value={liftGateRequired}
                                                onChangeHandler={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                    setLiftGateRequired(event.target.value);
                                                }}
                                            >
                                                <Radio value="True">{translate("Yes")}</Radio>
                                                <Radio value="False">{translate("No")}</Radio>
                                            </RadioGroup>

                                            {!isFreightValid && (
                                                <Notify
                                                    hideCloseButton={true}
                                                    messageType="danger"
                                                    body={siteMessage(
                                                        "Custom_FreightNotValid",
                                                        "Please answer the questions below.",
                                                    )}
                                                />
                                            )}

                                            <Typography variant="p" {...styles?.xStyle?.freightMessage}>
                                                {siteMessage("Custom_LtlFreightMessage")}
                                            </Typography>
                                        </StyledWrapper>
                                    )}
                            </GridItem>
                            {selectedCarrier?.shipVias && selectedCarrier?.shipVias?.length < 1 && (
                                <GridItem {...styles.serviceGridItem}>
                                    <Select
                                        label={translate("Select Service")}
                                        {...styles.serviceSelect}
                                        value={selectedShipVia?.id || ""}
                                        onChange={shipViaChangeHandler}
                                        data-test-selector="checkoutReviewAndSubmit_shippingServiceSelect"
                                    >
                                        {selectedCarrier &&
                                            selectedCarrier.shipVias!.map(s => {
                                                const id = s.id.toString();
                                                return (
                                                    <option key={id} value={id}>
                                                        {s.description}
                                                    </option>
                                                );
                                            })}
                                    </Select>
                                </GridItem>
                            )}
                        </>
                    )}
                    <GridItem {...styles?.xStyle?.deliveryDateContainer}>
                        <StyledWrapper {...styles?.xStyle?.deliveryDateWrapper}>
                            <Typography variant="h5">{translate("Delivery Date")}</Typography>
                            <Typography variant="p">
                                {siteMessage(
                                    "Custom_Delivery_Date_Message",
                                    "Delivery dates are subject to transport lines and truck locations. If you need expedited shipping, please inform us in the Shipping Notes field below.",
                                )}
                            </Typography>
                        </StyledWrapper>
                    </GridItem>

                    {cartSettings.canRequestDeliveryDate && (
                        <GridItem
                            {...styles.deliveryDateGridItem}
                            data-test-selector="checkoutShippingRequestedDeliveryDate"
                        >
                            <DatePicker
                                label={`${translate("Request Delivery Date")} (${translate("optional")})`}
                                hint={siteMessage("Checkout_RequestedDeliveryDateInformation")}
                                {...styles.deliveryDatePicker}
                                selectedDay={requestedDeliveryDate || cart.requestedDeliveryDateDisplay!}
                                dateTimePickerProps={{
                                    minDate: new Date(),
                                    maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                    monthAriaLabel: translate("Request delivery date month"),
                                    dayAriaLabel: translate("Request delivery date day"),
                                    yearAriaLabel: translate("Request delivery date year"),
                                    ...styles.deliveryDatePicker?.dateTimePickerProps,
                                }}
                                onDayChange={handleRequestDeliveryDateChanged}
                            />
                        </GridItem>
                    )}
                </>
            )}
            {showPickUpDate && currentStep === 3 && (
                <>
                    <GridItem {...styles.pickUpDateGridItem} data-test-selector="checkoutPickUpRequestedDeliveryDate">
                        <DatePicker
                            label={`${translate("Request Pick Up Date")} (${translate("optional")})`}
                            hint={siteMessage("Checkout_RequestedPickupDateInformation")}
                            {...styles.pickUpDatePicker}
                            selectedDay={requestedPickupDate || cart.requestedPickupDateDisplay!}
                            dateTimePickerProps={{
                                minDate: new Date(),
                                maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                ...styles.pickUpDatePicker?.dateTimePickerProps,
                            }}
                            onDayChange={handleRequestPickUpDateChanged}
                            monthAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                "Enter month, format M M",
                            )}`}
                            dayAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                "Enter day, format D D",
                            )}`}
                            yearAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                "Enter year, format Y Y Y Y",
                            )}`}
                        />
                    </GridItem>
                </>
            )}
            {((showShippingMethod && currentStep === 2) || (showPickUpDate && currentStep === 3)) && (
                <>
                    <GridItem {...styles?.xStyle?.notifyWrapper} {...styles.customNotifyWrapper}>
                        {shipComplete && (
                            <Notify
                                messageType="warning"
                                body={siteMessage(
                                    "Custom_ShipCompleteMessage",
                                    "There is no partial shipment. Product will not ship till everything is in stock to ship. ",
                                )}
                                hideCloseButton
                                extendedStyles={{
                                    cssOverrides: {
                                        toast: css`
                                            && {
                                                width: 100%;
                                                margin: 0;
                                                margin-bottom: 10px;
                                            }
                                        `,
                                    },
                                }}
                            />
                        )}
                        <Notify
                            messageType="warning"
                            body={siteMessage(
                                "Custom_ShippingCostCalcMessage",
                                "Shipping Costs Calculated Upon Completion of Order",
                            )}
                            hideCloseButton
                            extendedStyles={{
                                cssOverrides: {
                                    toast: css`
                                        && {
                                            width: 100%;
                                            margin: 0;
                                        }
                                    `,
                                },
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles?.xStyle?.shippingNotesWrapper}>
                        <TextArea
                            {...styles?.xStyle?.shippingNotesTextArea}
                            label={translate("Shipping Notes ( Optional )")}
                            placeholder={translate(
                                "Please add any additional notes, requirements or need for expedited shipping here",
                            )}
                            value={deliveryMessaging || cart.properties.deliveryMessaging}
                            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                                setDeliveryMessaging(event.currentTarget.value);
                            }}
                        />
                    </GridItem>
                </>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitCarrierService),
    definition: {
        displayName: "Carrier & Service",
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
    },
};

export default widgetModule;
